import React, { useState } from 'react'
import type { Theme } from '@material-ui/core'
import { makeStyles } from '@material-ui/core'
import classNames from 'classnames'
import type { StyleClasses } from '../../styles'
import Avatar from '../Avatar/Avatar'
import type { AvatarProps } from '../Avatar/Avatar'
import Tooltip from '../Tooltip'

const useStyles = makeStyles<Theme, EstimatorAvatarProps>(
  () => ({
    img: {
      borderRadius: '50%',
      objectFit: 'cover',
      height: ({ height }) => `${height || '28px'} !important`,
      width: ({ width }) => `${width || '28px'} !important`,
      marginRight: '0px !important'
    },
    avatar: {
      padding: '0px !important',
      boxShadow: ({ borderColor }) => (borderColor ? `0 0 0 2px white, 0 0 0 4px ${borderColor} !important` : undefined)
    }
  }),
  { name: 'EstimatorAvatar' }
)

export interface EstimatorAvatarUser {
  id?: string
  firstName?: string
  lastName?: string
}

export interface EstimatorAvatarProps extends AvatarProps {
  classes?: StyleClasses<typeof useStyles>
  userPictures: {
    [key: string]: string
  }
  user?: EstimatorAvatarUser
  borderColor?: string
  hideTooltip?: boolean
}

function addAuth0Prefix(id: string) {
  if (!id) return id
  return id.includes('auth0|') ? id : `auth0|${id}`
}

export default function EstimatorAvatar(props: EstimatorAvatarProps) {
  const { userPictures, user, borderColor, hideTooltip, ...avatarProps } = props
  const classes = useStyles(props)
  const userPicture = user?.id ? userPictures?.[addAuth0Prefix(user.id)] : null
  const userName = user?.id
    ? `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim() || 'PaintScout User'
    : 'PaintScout User'

  const [imgLoading, setImgLoading] = useState(false)

  const avatarColor = borderColor ?? props.avatarColor

  const Picture = userPicture ? (
    <img
      onClick={props.onClick}
      className={classNames(classes.img, 'disable-click')}
      src={userPicture}
      style={{
        opacity: imgLoading ? 0 : 1,
        transition: 'opacity 0.2s ease-in',
        boxShadow: borderColor
          ? `0 0 0 2px white, 0 0 0 4px ${borderColor}` // White outline followed by colored outline
          : undefined
      }}
      alt={userName}
      onLoad={() => {
        setImgLoading(false)
      }}
    />
  ) : (
    <Avatar
      className={classNames(classes.avatar, 'disable-click')}
      backgroundColor="#FFFFFF"
      avatarColor={avatarColor}
      disableTooltip
      filled
      noTransform
      width="28px"
      height="28px"
      fontSize={'12px'}
      {...avatarProps}
    >
      {/* Limit the username to two words so we only get two initials */}
      {userName.toUpperCase().split(/\s+/).slice(0, 2).join(' ')}
    </Avatar>
  )

  if (hideTooltip) {
    return Picture
  }
  return (
    <Tooltip hideIcon content={userName} enterDelay={500} enterNextDelay={500}>
      {Picture}
    </Tooltip>
  )
}
